import { reactive, toRaw, toValue, watch, type MaybeRefOrGetter } from 'vue'

import { compactIds } from './ids'

/**
 * Local editing buffer for a controlled filter form.
 *
 * The URL owns the filters, so the form never writes them directly: it edits a draft
 * and hands a snapshot back through `apply`. The draft is re-seeded whenever the
 * incoming values change, so external navigation — a deep link, the back button, a
 * reset from the page — updates the fields.
 *
 * Seeding always starts from `initial`, never from the previous draft: a key the new
 * values omit has to fall back to its empty value rather than keep a stale entry.
 */
export function useFilterDraft<T extends object>(
  initial: T,
  current: MaybeRefOrGetter<Partial<T> | undefined>
) {
  const draft = reactive({ ...initial, ...toValue(current) }) as T

  watch(
    () => toValue(current),
    (value) => {
      Object.assign(draft, { ...initial, ...value })
    }
  )

  /** A plain copy to emit — the draft itself stays reactive and owned by the form. */
  function snapshot(): T {
    return { ...toRaw(draft) }
  }

  function reset() {
    Object.assign(draft, initial)
  }

  return { draft, snapshot, reset }
}

/**
 * Wrap a filter value for a remote `Select`'s `:selected`.
 *
 * A remote select only lists what the last search returned, so a value that arrived
 * from the URL has no matching option and renders blank. Passing it here keeps the
 * backend from dropping it out of the option list.
 */
export function toSelectedIds<T extends string | number>(
  value: T | T[] | null | undefined
): T[] {
  if (Array.isArray(value)) return compactIds(value) as T[]

  return value == null || value === '' ? [] : (compactIds([value]) as T[])
}
